import { rupeesToPaise } from './money.js';

export interface FlatChoice {
  id: string;
  title: string;
  society: string | null;
  flatNo: string | null;
}

export interface PastedRow {
  line: number;
  row: {
    tenantName: string;
    tenantPhone: string;
    ownerName: string | null;
    ownerPhone: string | null;
    propertyId: string;
    rentPaise: number;
    startDate: string;
    endDate: string;
    brokeragePaise: number | null;
  } | null;
  error: string | null;
}

/** `19/11/2026`, `19-11-2026` or `2026-11-19` as `2026-11-19`, or null. */
export function parseDate(text: string): string | null {
  const t = text.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(t);
  if (m === null) return null;
  const [, d, mo, y] = m as unknown as [string, string, string, string];
  const iso = `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== iso ? null : iso;
}

const norm = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/** The flat a pasted name means: its title, or `flat no, society`, case and spacing aside. */
export function matchFlat(text: string, flats: FlatChoice[]): FlatChoice | null {
  const wanted = norm(text);
  if (wanted === '') return null;
  return (
    flats.find((f) => norm(f.title) === wanted) ??
    flats.find((f) => norm([f.flatNo, f.society].filter(Boolean).join(', ')) === wanted) ??
    null
  );
}

/**
 * Pasted rows, one tenancy a line, separated by tabs (from Excel) or commas:
 * tenant name, tenant mobile, owner name, owner mobile, flat, rent, start, end, brokerage.
 */
export function parsePastedTenancies(text: string, flats: FlatChoice[]): PastedRow[] {
  const out: PastedRow[] = [];
  text.split('\n').forEach((raw, i) => {
    if (raw.trim() === '') return;
    const parts = (raw.includes('\t') ? raw.split('\t') : raw.split(',')).map((p) => p.trim());
    const fail = (error: string): void => {
      out.push({ line: i + 1, row: null, error });
    };
    if (parts.length < 8)
      return fail(
        'Needs at least 8 columns: tenant, tenant mobile, owner, owner mobile, flat, rent, start, end',
      );
    const [tenantName, tenantPhone, ownerName, ownerPhone, flat, rent, start, end, brokerage] =
      parts as [string, string, string, string, string, string, string, string, string | undefined];
    const match = matchFlat(flat, flats);
    if (match === null) return fail(`The flat "${flat}" is not in your list. Add it first.`);
    const rentPaise = rupeesToPaise(rent);
    if (rentPaise === null || rentPaise <= 0) return fail('The rent is not an amount');
    const startDate = parseDate(start);
    const endDate = parseDate(end);
    if (startDate === null || endDate === null)
      return fail('The dates should read like 19/11/2026');
    const brokeragePaise =
      brokerage === undefined || brokerage === '' ? null : rupeesToPaise(brokerage);
    if (brokerage !== undefined && brokerage !== '' && brokeragePaise === null) {
      return fail('The brokerage is not an amount');
    }
    out.push({
      line: i + 1,
      row: {
        tenantName,
        tenantPhone,
        ownerName: ownerName === '' ? null : ownerName,
        ownerPhone: ownerPhone === '' ? null : ownerPhone,
        propertyId: match.id,
        rentPaise,
        startDate,
        endDate,
        brokeragePaise,
      },
      error: null,
    });
  });
  return out;
}
